<template>
  <div class="file-upload">
    <div
      class="file-upload__list"
      :style="{ 'grid-template-columns': `repeat(${rows}, 1fr)` }"
    >
      <!-- Uploads list -->
      <div
        class="file-upload__element"
        v-for="(upload, i) in value"
        :key="`file-upload__element--${i}`"
      >
        <i
          class="file-upload__element__icon"
          :class="getUploadIcon(upload)"
        />
        <span
          class="file-upload__element__name"
          v-text="upload.name"
        />
        <i
          class="file-upload__element__icon icon-cross"
          @click="onRemove(upload)"
        />
      </div>

      <!-- Upload button -->
      <el-upload
        v-show="!isFull"
        class="file-upload__button"
        :accept="accept && accept.join(',')"
        :http-request="uploadFromRequest"
        :limit="max"
        :auto-upload="autoUpload"
        :multiple="multiple"
        :on-change="onSelect"
        :on-success="onSuccess"
        :on-exceed="onExceed"
        action
        :show-file-list="false"
        ref="files"
      >
        <el-button
          size="small"
          type="default"
        >
          <i class="icon-upload" />
          <app-i18n code="fileUploader.uploadFile" />
        </el-button>
      </el-upload>
    </div>
    <div
      class="file-upload__message"
      v-text="message"
    />
  </div>
</template>

<script>
import { i18n } from '@shared/i18n';
import { FileUploader } from '@shared/firebase/file-uploader';

export default {
  name: 'app-file-upload',
  props: {
    path: String,
    value: Array,
    accept: Array,
    schema: Object,
    max: Number,
    placeholder: String,
    autoUpload: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 5,
    }
  },
  data() {
    return {
      message: null,
      uploadPromises: [],
    };
  },
  computed: {
    isFull() {
      return this.fileList.length >= this.max;
    },

    fileList(){
      return (this.value || []).map((item) => ({
        ...item,
        url: item.publicUrl,
      }));
    },

    isUploading() {
      return !!this.uploadPromises.length;
    },
  },

  methods: {
    async uploadFromRequest(request) {
      let resolvePromise, rejectPromise;
 
      const uploadPromise = new Promise((resolve, reject) => {
        resolvePromise = resolve
        rejectPromise = reject
      });

      FileUploader.uploadFromRequest(
        this.path,
        request,
        this.schema,
        { addIdToPath: true },
        error => {
          if (error) rejectPromise(error)
          else resolvePromise()
        },
      );

      if (resolvePromise) this.uploadPromises = [...this.uploadPromises, uploadPromise];
    },

    onSelect(file, fileList) {
      if (this.autoUpload) return;
      if (this.isUploading) return;
      if (!file) return;
      if (file.response) return;
      if (file.publicUrl) return;
      if (!fileList && !fileList.length) return;

      try {
        FileUploader.validate(file, this.schema)
        this.$emit('input', [...fileList]);
      } catch (error) {
        const filesRef = this.$refs.files;
        filesRef.handleRemove(file, file.raw);
        this.message = error.message
      }
    },

    onSuccess(raw, response) {
      const upload = 'response' in response ? response.response : response;
      if (upload) this.$emit('input', [...(this.value || []), upload]);
    },

    onExceed(files, fileList) {
      const filesLeft = this.max - fileList.length

      if (filesLeft === 1) this.message = i18n('fileUploader.exceed')
      else this.message = i18n('fileUploader.exceedPlural', filesLeft)
    },

    onRemove(file) {
      const filesRef = this.$refs.files;
      filesRef.handleRemove(file, file.raw);
      this.$emit('input', [...(filesRef.uploadFiles || [])]);
    },

    getUploadIcon(upload) {
      const fileType = upload.raw ? upload.raw.type : upload.type;
      const imageTypes = ['image/gif', 'image/jpeg', 'image/png'];
      const isImage = imageTypes.includes(fileType);
      if (isImage) return 'icon-image';
      return 'icon-document';
    },

    async upload() {
      const uploadedImages = this.value.filter(image => image.response || image.publicUrl);
      this.$emit('input', uploadedImages);

      try {
        await this.$refs.files.submit();
        await this.$nextTick();
        await Promise.all(this.uploadPromises);
      } catch (e) {
        throw e;
      } finally {
        this.uploadPromises = [];
      }
    }
  },
  watch: {
    fileList() {
      this.message = null;
    },
    isFull: {
      handler(val) {
        if (val) this.message = i18n('fileUploader.maxReached', this.max);
        else this.message = null;
      },
      immediate: true,
    },
  }
};
</script>

<style>
.file-upload {
  --item-height: 40px;
  min-height: var(--item-height);
}

.file-upload .el-upload,
.file-upload__button {
  height: var(--item-height);
}

.file-upload__list {
  display: grid;
  grid-gap: 8px;
}

@media (max-width: 768px) {
  .file-upload__list {
    grid-template-columns: 1fr 1fr !important;
  }
}

.file-upload__element {
  height: var(--item-height);
  margin: 0;
  padding: 0 16px;

  box-sizing: border-box;
  border-radius: 20px;
  border: 1px solid #c5c5c5;
  background-color: #ffffff;
  cursor: default;

  display: grid;
  grid-template-columns: 24px 1fr 10px;
  align-items: center;
  text-align: left;
  grid-gap: 8px; 
}

.file-upload__element__icon {
  font-size: 18px;
}

.file-upload__element__icon.icon-cross {
  font-size: 10px;
  cursor: pointer;
}

.file-upload__element__icon.icon-cross:hover {
  color: #2acccc;
}

.file-upload__element__name {
  font-size: 14px;
  font-weight: normal;
  letter-spacing: -0.3px;
  color: #404040;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-upload__message {
  position: absolute;
  font-size: 12px;
  color: #404040;
  line-height: 1;
  margin-top: 10px;
  margin-left: 10px;
}

@media (max-width: 768px) {
  .file-upload__message {
    margin-top: 14px;
  }
}

.file-upload .file-upload__list .file-upload__button {
  width: 100%;
  margin: 0;
}

.file-upload .file-upload__list .file-upload__button .el-upload {
  width: 100%;
}

.file-upload .file-upload__list .file-upload__button .el-button {
  width: 100%;
  height: var(--item-height);
  border-radius: 20px;
  border: 1px dashed #c5c5c5;
  background-color: rgba(197, 197, 197, 0.2);

  font-size: 14px;
  font-weight: normal;
  letter-spacing: -0.3px;
  color: #404040;
  text-transform: none;
  padding: 0;
}

.file-upload .file-upload__list .file-upload__button .el-button > span {
  display: grid;
  grid-template-columns: 24px 1fr;
  padding: 0 18px;
  align-items: center;
  text-align: left
}

.file-upload .file-upload__list .file-upload__button .el-button span .icon-upload {
  font-size: 16px;
}
</style>

